﻿import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import {
	LayoutDashboard,
	Package,
	History,
	Calendar,
	ShoppingCart,
	Settings,
	CalendarDays,
	FileText,
	ChevronRight,
	ChevronDown,
	CircleDot,
} from "lucide-react";

import { CHANGE_SELECT_INVENTORIES } from "@/redux/types/inventory.types";
import { handleSidebar } from "@/redux/actions/main.actions";
import { textConstants } from "@/languages/textConstants";

import classes from "./sidebar.module.scss";

const getIcon = (iconName) => {
	const iconProps = {
		size: 20,
		strokeWidth: 2,
		className: classes.icon,
	};

	const iconMap = {
		CalendarSvg: <CalendarDays {...iconProps} />,
		InventorySvg: <Package {...iconProps} />,
		PlanningSvg: <Calendar {...iconProps} />,
		HistorySvg: <History {...iconProps} />,
		UserSvg: <LayoutDashboard {...iconProps} />,
		NotificacionSvg: <History {...iconProps} />,
		NotificationAlertSvg: <History {...iconProps} />,
		PurchaseSvg: <ShoppingCart {...iconProps} />,
		MaintainersSvg: <Settings {...iconProps} />,
		ReportSvg: <FileText {...iconProps} />,
	};

	return iconMap[iconName] || <LayoutDashboard {...iconProps} />;
};

const getSubmenuIcon = () => {
	return <CircleDot size={14} strokeWidth={2} />;
};

const isPlainLeftClick = (event) => {
	return event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
};

const SideBar = () => {
	const location = useLocation();
	const dispatch = useDispatch();

	const { is_implementation_completed } = localStorage.getItem("imp_state")
		? JSON.parse(localStorage.getItem("imp_state"))
		: { is_implementation_completed: true };

	const { openSidebar } = useSelector((state) => state.main);
	const { screens_option } = useSelector((state) => state.auth);
	const { customer } = useSelector((state) => state.auth.user);

	const [apps, setApps] = useState(screens_option);
	const [expandedMenus, setExpandedMenus] = useState({});
	const [pinnedMenus, setPinnedMenus] = useState({});
	const [pinnedIndex, setPinnedIndex] = useState(null);
	const [submenuPosition, setSubmenuPosition] = useState({});

	const closeAllMenus = () => {
		setPinnedMenus({});
		setExpandedMenus({});
		setPinnedIndex(null);
		setSubmenuPosition({});
	};

	const toggleMenu = (index) => {
		setExpandedMenus((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const handleMenuClick = (event, app, index, hasSubmenus) => {
		if (hasSubmenus) {
			if (!openSidebar) {
				const rect = event.currentTarget.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const maxHeight = viewportHeight - rect.top - 20;
				setSubmenuPosition((prev) => ({
					...prev,
					[index]: { top: rect.top, maxHeight },
				}));

				if (pinnedMenus[index]) {
					setPinnedMenus({});
					setExpandedMenus({});
					setPinnedIndex(null);
				} else {
					setPinnedMenus({ [index]: true });
					setExpandedMenus({ [index]: true });
					setPinnedIndex(index);
				}
			} else {
				toggleMenu(index);
			}
		}
	};

	const handleNavClick = (event) => {
		if (!isPlainLeftClick(event)) return;
		cleanInventoriesSelected();
		closeAllMenus();
	};

	const isMenuActive = (app) => {
		if (location.pathname === app.path) return true;
		if (app.screens) {
			return app.screens.some((screen) => location.pathname === screen.path);
		}
		return false;
	};

	const cleanInventoriesSelected = () => {
		dispatch({
			type: CHANGE_SELECT_INVENTORIES,
			payload: {
				selected: [],
			},
		});
	};

	useEffect(() => {
		setApps(screens_option);
	}, [screens_option, location.pathname]);

	useEffect(() => {
		if (openSidebar) {
			setPinnedMenus({});
			setExpandedMenus((prev) => prev);
			setPinnedIndex(null);
		}
	}, [openSidebar]);

	useEffect(() => {
		if (!openSidebar && pinnedIndex !== null) {
			const onDocClick = (e) => {
				const groupEl = e.target.closest("[data-menu-index]");
				const clickedIndex = groupEl ? parseInt(groupEl.dataset.menuIndex, 10) : null;
				if (clickedIndex !== pinnedIndex) {
					setPinnedMenus({});
					setExpandedMenus({});
					setPinnedIndex(null);
				}
			};
			document.addEventListener("click", onDocClick);
			return () => document.removeEventListener("click", onDocClick);
		}
	}, [openSidebar, pinnedIndex]);

	return (
		<>
			{openSidebar && <button type="button" className={classes.mobileBackdrop} aria-label="Cerrar menú" onClick={() => dispatch(handleSidebar(false))} />}
			<nav className={`${classes.sidebar} ${openSidebar ? classes.expanded : classes.collapsed}`}>
				<div className={classes.sidebarContent}>
					<Link to="/" className={`${classes.menuItem} ${location.pathname === "/" ? classes.active : ""}`} onClick={handleNavClick} title="Resumen">
						<LayoutDashboard size={20} strokeWidth={2} />
						{openSidebar && <span>Resumen</span>}
					</Link>

					{apps.map((app, i) => {
						if (app.screens && app.screens.length === 0) return null;
						if (!is_implementation_completed) return null;

						const hasSubmenus = app.screens && app.screens.length > 0;
						const isActive = isMenuActive(app);
						const isExpanded = expandedMenus[i];

						const menuItemClassName = `${classes.menuItem} ${isActive ? classes.active : ""}`;

						return (
							<div key={i} className={classes.menuGroup} data-menu-index={i}>
								{hasSubmenus ? (
									<button
										type="button"
										className={menuItemClassName}
										onClick={(e) => handleMenuClick(e, app, i, hasSubmenus)}
										title={app.description}
									>
										{getIcon(app.icon)}
										{openSidebar && (
											<>
												<span className={classes.menuLabel}>{app.description}</span>
												{isExpanded ? <ChevronDown size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={2} />}
											</>
										)}
									</button>
								) : (
									<Link to={app.path} className={menuItemClassName} onClick={handleNavClick} title={app.description}>
										{getIcon(app.icon)}
										{openSidebar && <span className={classes.menuLabel}>{app.description}</span>}
									</Link>
								)}

								{hasSubmenus && (
									<div
										className={`${classes.submenuContainer} ${isExpanded ? classes.submenuExpanded : ""} ${pinnedMenus[i] ? classes.submenuPinned : ""}`}
										style={
											!openSidebar && submenuPosition[i]
												? { top: `${submenuPosition[i].top}px`, maxHeight: `${submenuPosition[i].maxHeight}px` }
												: {}
										}
									>
										{app.screens
											.slice()
											.sort((a, b) => a.show_order - b.show_order)
											.map((item, j) => {
												let itemLabel = item.label;
												if (item.id === 20 && customer) {
													itemLabel = textConstants[customer.country_id]?.delivery || item.label;
												}

												return (
													<Link
														key={j}
														to={item.path}
														className={`${classes.submenuItem} ${location.pathname === item.path ? classes.active : ""}`}
														onClick={handleNavClick}
														title={itemLabel}
													>
														{getSubmenuIcon()}
														<span>{itemLabel}</span>
													</Link>
												);
											})}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</nav>
		</>
	);
};

export default SideBar;
